import {
    AssistantConversationMessagePayloadDto,
    AssistantFeedbackPayloadDto,
} from "@core/data/dto/assistant.dto";
import { AssistantConversation } from "@core/domain/models/assistant-conversation.model";
import { AssistantMessage } from "@core/domain/models/assistant-message.model";
import { DeleteAssistantConversationUseCase } from "@core/domain/usecases/delete-assistant-conversation.usecase";
import { GetAssistantConversationUseCase } from "@core/domain/usecases/get-assistant-conversation.usecase";
import { GetAssistantConversationsUseCase } from "@core/domain/usecases/get-assistant-conversations.usecase";
import { SendAssistantMessageUseCase } from "@core/domain/usecases/send-assistant-message.usecase";
import { StartAssistantConversationUseCase } from "@core/domain/usecases/start-assistant-conversation.usecase";
import { SubmitAssistantFeedbackUseCase } from "@core/domain/usecases/submit-assistant-feedback.usecase";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class AssistantPanelViewModel extends BaseViewModel {
    private nextTemporaryMessageId: number = -1;

    @observable
    conversations: AssistantConversation[] = [];

    @observable
    activeConversation: AssistantConversation | null = null;

    @observable
    loading: boolean = true;

    @observable
    sending: boolean = false;

    @observable
    draft: string = "";

    @observable
    error: string | null = null;

    @observable
    feedbackLoadingIds: number[] = [];

    @observable
    deletingConversationId: number | null = null;

    constructor(
        @inject(GetAssistantConversationsUseCase)
        private readonly getAssistantConversationsUseCase: GetAssistantConversationsUseCase,
        @inject(DeleteAssistantConversationUseCase)
        private readonly deleteAssistantConversationUseCase: DeleteAssistantConversationUseCase,
        @inject(GetAssistantConversationUseCase)
        private readonly getAssistantConversationUseCase: GetAssistantConversationUseCase,
        @inject(StartAssistantConversationUseCase)
        private readonly startAssistantConversationUseCase: StartAssistantConversationUseCase,
        @inject(SendAssistantMessageUseCase)
        private readonly sendAssistantMessageUseCase: SendAssistantMessageUseCase,
        @inject(SubmitAssistantFeedbackUseCase)
        private readonly submitAssistantFeedbackUseCase: SubmitAssistantFeedbackUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await super.didMount();
        await this.loadConversations();
    }

    async loadConversations(): Promise<void> {
        runInAction(() => {
            this.loading = true;
            this.error = null;
        });

        const result = await this.getAssistantConversationsUseCase.execute();
        if (result.isLeft()) {
            runInAction(() => {
                this.loading = false;
                this.error = "load";
            });
            return;
        }

        const conversations = result.getOrThrow();
        runInAction(() => {
            this.conversations = conversations;
            this.loading = false;
        });

        if (conversations.length > 0) {
            await this.openConversation(conversations[0].id);
        }
    }

    @action
    setDraft(draft: string): void {
        this.draft = draft;
    }

    async openConversation(id: number): Promise<void> {
        const result = await this.getAssistantConversationUseCase.execute(id);
        if (result.isLeft()) {
            runInAction(() => {
                this.error = "load";
            });
            return;
        }

        const conversation = result.getOrThrow();
        runInAction(() => {
            this.activeConversation = conversation;
            this.upsertConversation(conversation);
        });
    }

    async deleteConversation(id: number): Promise<void> {
        if (this.deletingConversationId !== null) {
            return;
        }

        runInAction(() => {
            this.deletingConversationId = id;
            this.error = null;
        });

        const result =
            await this.deleteAssistantConversationUseCase.execute(id);
        if (result.isLeft()) {
            runInAction(() => {
                this.deletingConversationId = null;
                this.error = "delete";
            });
            return;
        }

        const wasActiveConversation = this.activeConversation?.id === id;
        const remainingConversations = this.conversations.filter(
            (conversation) => conversation.id !== id,
        );
        const nextConversation = remainingConversations[0] ?? null;

        runInAction(() => {
            this.conversations = remainingConversations;
            this.deletingConversationId = null;
            if (wasActiveConversation) {
                this.activeConversation = null;
            }
        });

        if (wasActiveConversation && nextConversation !== null) {
            await this.openConversation(nextConversation.id);
        }
    }

    async startConversation(
        screenContext?: Record<string, unknown>,
    ): Promise<AssistantConversation | null> {
        const result = await this.startAssistantConversationUseCase.execute(
            screenContext
                ? {
                      screen_context: screenContext,
                  }
                : undefined,
        );
        if (result.isLeft()) {
            runInAction(() => {
                this.error = "load";
            });
            return null;
        }

        const conversation = result.getOrThrow();
        runInAction(() => {
            this.activeConversation = conversation;
            this.conversations = [conversation, ...this.conversations];
        });
        return conversation;
    }

    async sendMessage(
        screenContext?: Record<string, unknown>,
        presetMessage?: string,
    ): Promise<void> {
        const content = (presetMessage ?? this.draft).trim();
        if (!content || this.sending) {
            return;
        }

        runInAction(() => {
            this.sending = true;
            this.error = null;
            if (!presetMessage) {
                this.draft = "";
            }
        });

        let conversation = this.activeConversation;
        if (conversation === null) {
            conversation = await this.startConversation(screenContext);
            if (conversation === null) {
                runInAction(() => {
                    this.sending = false;
                });
                return;
            }
        }

        const baseConversation = this.activeConversation ?? conversation;
        const temporaryUserMessage = this.buildTemporaryMessage(
            "user",
            content,
        );
        const temporaryAssistantMessage = this.buildTemporaryMessage(
            "assistant",
            "",
            true,
        );

        runInAction(() => {
            const optimisticConversation = this.cloneConversation(
                baseConversation,
                [
                    ...baseConversation.messages,
                    temporaryUserMessage,
                    temporaryAssistantMessage,
                ],
                {
                    title: baseConversation.title || content,
                    screenContext:
                        screenContext ?? baseConversation.screenContext,
                    updatedAt: DateTime.now(),
                    lastMessagePreview: content,
                },
            );

            this.activeConversation = optimisticConversation;
            this.upsertConversation(optimisticConversation);
        });

        const payload: AssistantConversationMessagePayloadDto = screenContext
            ? {
                  content,
                  screen_context: screenContext,
              }
            : {
                  content,
              };
        const result = await this.sendAssistantMessageUseCase.execute(
            conversation.id,
            payload,
        );

        if (result.isLeft()) {
            runInAction(() => {
                if (this.activeConversation !== null) {
                    const revertedMessages =
                        this.activeConversation.messages.filter(
                            (message) =>
                                message.id !== temporaryUserMessage.id &&
                                message.id !== temporaryAssistantMessage.id,
                        );
                    const revertedConversation = this.cloneConversation(
                        this.activeConversation,
                        revertedMessages,
                        {
                            updatedAt: this.activeConversation.updatedAt,
                            lastMessagePreview:
                                revertedMessages.at(-1)?.content ?? "",
                        },
                    );
                    this.activeConversation = revertedConversation;
                    this.upsertConversation(revertedConversation);
                }
                this.sending = false;
                this.error = "send";
                this.draft = content;
            });
            return;
        }

        const exchange = result.getOrThrow();
        runInAction(() => {
            const persistedMessages = (
                this.activeConversation?.messages ?? []
            ).filter(
                (message) =>
                    message.id !== temporaryUserMessage.id &&
                    message.id !== temporaryAssistantMessage.id,
            );
            const nextConversation = new AssistantConversation(
                exchange.conversation.id,
                exchange.conversation.title,
                exchange.conversation.screenContext,
                exchange.conversation.createdAt,
                exchange.conversation.updatedAt,
                exchange.conversation.lastMessagePreview,
                [
                    ...persistedMessages,
                    exchange.userMessage,
                    exchange.assistantMessage,
                ],
            );

            this.activeConversation = nextConversation;
            this.upsertConversation(nextConversation);
            this.sending = false;
        });
    }

    async submitFeedback(
        messageId: number,
        feedback: AssistantFeedbackPayloadDto,
    ): Promise<void> {
        runInAction(() => {
            this.feedbackLoadingIds = [...this.feedbackLoadingIds, messageId];
        });

        const result = await this.submitAssistantFeedbackUseCase.execute(
            messageId,
            feedback,
        );

        runInAction(() => {
            this.feedbackLoadingIds = this.feedbackLoadingIds.filter(
                (id) => id !== messageId,
            );
        });

        if (result.isLeft()) {
            return;
        }

        const updatedMessage = result.getOrThrow();
        runInAction(() => {
            if (this.activeConversation === null) {
                return;
            }

            this.activeConversation = new AssistantConversation(
                this.activeConversation.id,
                this.activeConversation.title,
                this.activeConversation.screenContext,
                this.activeConversation.createdAt,
                this.activeConversation.updatedAt,
                this.activeConversation.lastMessagePreview,
                this.activeConversation.messages.map((message) =>
                    message.id === updatedMessage.id ? updatedMessage : message,
                ),
            );
            this.upsertConversation(this.activeConversation);
        });
    }

    isFeedbackLoading(messageId: number): boolean {
        return this.feedbackLoadingIds.includes(messageId);
    }

    isDeletingConversation(id: number): boolean {
        return this.deletingConversationId === id;
    }

    private upsertConversation(conversation: AssistantConversation): void {
        const rest = this.conversations.filter(
            (item) => item.id !== conversation.id,
        );
        this.conversations = [conversation, ...rest].sort(
            (left, right) =>
                right.updatedAt.toMillis() - left.updatedAt.toMillis(),
        );
    }

    private buildTemporaryMessage(
        role: "user" | "assistant",
        content: string,
        pending: boolean = false,
    ): AssistantMessage {
        const message = new AssistantMessage(
            this.nextTemporaryMessageId,
            role,
            content,
            [],
            {
                pending,
                temporary: true,
            },
            null,
            "",
            null,
            DateTime.now(),
        );

        this.nextTemporaryMessageId -= 1;
        return message;
    }

    private cloneConversation(
        conversation: AssistantConversation,
        messages: AssistantMessage[],
        overrides?: {
            title?: string;
            screenContext?: Record<string, unknown> | null;
            updatedAt?: DateTime;
            lastMessagePreview?: string;
        },
    ): AssistantConversation {
        return new AssistantConversation(
            conversation.id,
            overrides?.title ?? conversation.title,
            overrides?.screenContext ?? conversation.screenContext,
            conversation.createdAt,
            overrides?.updatedAt ?? conversation.updatedAt,
            overrides?.lastMessagePreview ?? conversation.lastMessagePreview,
            messages,
        );
    }
}
